import React, { useState, useEffect, useCallback } from "react";
import { RefreshCwIcon } from "lucide-react";
import { Button } from "../ui/button";
import { PostItem } from "./PostItem";
import {
  getPosts,
  subscribeToPosts,
  getCurrentFriends,
  getSentFriendRequests,
  getPendingFriendRequests,
  addFriend,
  acceptFriendRequest,
  Post,
  Friend,
} from "../../lib/supabase";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

interface FeedListProps {
  refreshTrigger?: number;
  onFriendRequestAccepted?: () => void;
}

export const FeedList: React.FC<FeedListProps> = ({
  refreshTrigger,
  onFriendRequestAccepted,
}) => {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [currentFriends, setCurrentFriends] = useState<Friend[]>([]);
  const [sentRequests, setSentRequests] = useState<Friend[]>([]);
  const [pendingRequests, setPendingRequests] = useState<Friend[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const POSTS_PER_PAGE = 10;

  // Load friend statuses (current friends, sent requests, pending requests)
  const loadFriendStatuses = useCallback(async () => {
    if (!user?.id) return;

    try {
      const [friendsResult, sentResult, pendingResult] = await Promise.all([
        getCurrentFriends(user.id),
        getSentFriendRequests(user.id),
        getPendingFriendRequests(user.id),
      ]);

      if (friendsResult.success && friendsResult.data) {
        setCurrentFriends(friendsResult.data);
      }

      if (sentResult.success && sentResult.data) {
        setSentRequests(sentResult.data);
      }

      if (pendingResult.success && pendingResult.data) {
        setPendingRequests(pendingResult.data);
      }
    } catch (error) {
      console.error("Error loading friend statuses:", error);
    }
  }, [user?.id]);

  // Load posts with pagination
  const loadPosts = useCallback(
    async (pageNum: number = 0, append: boolean = false) => {
      try {
        if (pageNum === 0) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const offset = pageNum * POSTS_PER_PAGE;
        const result = await getPosts(POSTS_PER_PAGE, offset);

        if (result.success && result.data) {
          if (append) {
            setPosts(prev => [...prev, ...result.data!]);
          } else {
            setPosts(result.data);
          }

          // Check if there are more posts
          setHasMore(result.data.length === POSTS_PER_PAGE);
          setPage(pageNum);
        } else {
          toast.error(result.error || "Gagal memuat posts");
        }
      } catch (error) {
        console.error("Error loading posts:", error);
        toast.error("Terjadi kesalahan saat memuat posts");
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Load more posts for infinite scroll
  const loadMorePosts = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadPosts(page + 1, true);
    }
  }, [loadPosts, page, loadingMore, hasMore]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      // Check if user has scrolled near the bottom (within 1000px)
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 1000
      ) {
        loadMorePosts();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [loadMorePosts]);

  // Initial load and refresh trigger
  useEffect(() => {
    loadPosts(0, false);
    loadFriendStatuses();
  }, [loadPosts, loadFriendStatuses, refreshTrigger]);

  // Set up real-time subscription
  useEffect(() => {
    const subscription = subscribeToPosts(newPost => {
      setPosts(prev => [newPost, ...prev]);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleAddFriend = async (userId: string) => {
    if (!user?.id) return;

    try {
      const result = await addFriend(user.id, userId);

      if (result.success) {
        toast.success("Permintaan pertemanan berhasil dikirim");
        // Refresh friend statuses to update UI
        await loadFriendStatuses();
      } else {
        toast.error(result.error || "Gagal menambahkan teman");
      }
    } catch (error) {
      console.error("Error adding friend:", error);
      toast.error("Terjadi kesalahan saat menambahkan teman");
    }
  };

  const handleAcceptFriendRequest = async (
    requestId: string,
    senderId: string
  ) => {
    if (!user?.id) return;

    try {
      const result = await acceptFriendRequest(requestId, user.id, senderId);

      if (result.success) {
        toast.success("Permintaan pertemanan diterima");
        // Refresh friend statuses to update UI
        await loadFriendStatuses();

        // Notify parent component to refresh friend lists
        if (onFriendRequestAccepted) {
          onFriendRequestAccepted();
        }
      } else {
        toast.error(result.error || "Gagal menerima permintaan pertemanan");
      }
    } catch (error) {
      console.error("Error accepting friend request:", error);
      toast.error("Terjadi kesalahan saat menerima permintaan pertemanan");
    }
  };

  const handleInviteCollaborator = () => {
    // TODO: Implement invite collaborator functionality
    toast("Fitur invite collaborator akan segera tersedia");
  };

  const handleRefresh = () => {
    loadPosts(0, false);
    loadFriendStatuses();
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-[#373e46]">
            All Post
          </h2>
        </div>

        {/* Loading skeleton */}
        {[1, 2, 3].map(i => (
          <div
            key={i}
            className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200 animate-pulse"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-[#373e46]">
          All Post
        </h2>
        <Button
          onClick={handleRefresh}
          variant="outline"
          size="sm"
          className="flex items-center gap-2 text-[#373e46] border-gray-300 hover:bg-gray-50 h-8 px-3 text-xs"
        >
          <RefreshCwIcon className="w-3 h-3" />
          <span className="hidden sm:inline">Refresh</span>
        </Button>
      </div>

      {/* Posts */}
      {posts.length > 0 ? (
        <>
          {posts.map(post => (
            <PostItem
              key={post.id}
              post={post}
              currentUserId={user?.id}
              currentFriends={currentFriends}
              sentRequests={sentRequests}
              pendingRequests={pendingRequests}
              onAddFriend={handleAddFriend}
              onAcceptFriendRequest={handleAcceptFriendRequest}
              onInviteCollaborator={handleInviteCollaborator}
            />
          ))}

          {/* Loading indicator for infinite scroll */}
          {loadingMore && (
            <div className="flex justify-center py-8">
              <div className="flex items-center gap-3 text-[#373e46]">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#6b2cfe]"></div>
                <span className="text-sm">Loading more posts...</span>
              </div>
            </div>
          )}

          {/* End of posts indicator */}
          {!hasMore && posts.length > 0 && (
            <div className="text-center py-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span className="text-sm text-gray-600">
                  You&apos;ve reached the end
                </span>
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Empty State */
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <RefreshCwIcon className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-lg font-semibold text-[#373e46] mb-2">
            Belum Ada Post
          </h4>
          <p className="text-gray-500 mb-4">
            Jadilah yang pertama membagikan konten!
          </p>
          <Button
            onClick={handleRefresh}
            className="bg-gradient-to-r from-[#6b2cfe] to-[#0217ff] hover:opacity-90 text-white px-6 py-2 rounded-xl font-medium transition-all duration-300 hover:shadow-lg"
          >
            Refresh
          </Button>
        </div>
      )}
    </div>
  );
};
